import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Upload, X, CheckCircle, Camera, Clock, Shield, Droplets } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_business-match-hub-1/artifacts/ly54u1mk_7A4F7B0F-B2FC-4B29-8962-F57D33FF73A6.png";
const DEMO_VIDEO = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/f5j74oaa_Dry Ice cleaning video.mp4";
const VIDEO_POSTER = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/fph3vzj9_IMG_0924.PNG";

// Service images
const serviceImages = {
  fleet: "https://images.unsplash.com/photo-1594573677313-757d4aeea449?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  fire: "https://images.unsplash.com/photo-1748816964372-8a73dbb4bb7a?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  mold: "https://images.unsplash.com/photo-1649777882133-525e923fd5d7?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  equipment: "https://images.unsplash.com/photo-1768054948628-82319724f0b7?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  industrial: "https://images.unsplash.com/photo-1756561909277-92753f05f032?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  concrete: "https://images.unsplash.com/photo-1771531072574-af6ed6b954c0?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
};

const services = [
  { id: 'fleet', name: 'Fleet & Vehicles', img: serviceImages.fleet },
  { id: 'fire', name: 'Fire Damage', img: serviceImages.fire },
  { id: 'mold', name: 'Mold Remediation', img: serviceImages.mold },
  { id: 'equipment', name: 'Equipment', img: serviceImages.equipment },
  { id: 'industrial', name: 'Industrial', img: serviceImages.industrial },
  { id: 'other', name: 'Other', img: serviceImages.concrete },
];

export const QRLandingPage = () => {
  const [selectedService, setSelectedService] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    description: '',
    preferred_contact: 'phone',
    urgency: 'standard'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImages(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService) {
      setSubmitError('Please select a service type');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Convert images to base64 URLs for storage
      const imageUrls = uploadedImages.map(img => img.preview);
      
      await axios.post(`${API}/estimate`, {
        ...formData,
        service_type: selectedService,
        image_urls: imageUrls
      });
      
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError('Failed to submit. Please call us directly at (817) 210-9693');
      console.error('Estimate form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-zinc-950 border border-zinc-800 p-8 md:p-12 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-black" />
          </div>
          <h1 className="text-4xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Request Received!
          </h1>
          <p className="text-zinc-300 mb-6">
            We'll review your request and get back to you within 24 hours with a free estimate.
          </p>
          <div className="bg-black border border-zinc-800 p-6 mb-6">
            <p className="text-zinc-400 text-sm mb-2">Need faster response?</p>
            <a href="tel:8172109693" className="text-2xl text-orange-500 font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              (817) 210-9693
            </a>
          </div>
          <button
            onClick={() => {
              setSubmitSuccess(false);
              setSelectedService('');
              setUploadedImages([]);
              setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                description: '',
                preferred_contact: 'phone',
                urgency: 'standard'
              });
            }}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            Submit Another Request
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="qr-landing-page">
      {/* Header */}
      <header className="bg-black border-b border-zinc-800 p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="23rd Industries" className="h-10" />
          <a href="tel:8172109693" className="flex items-center gap-2 text-orange-500">
            <Phone className="w-4 h-4" />
            <span className="font-mono text-sm">(817) 210-9693</span>
          </a>
        </div>
      </header>

      {/* Hero Video */}
      <section className="relative">
        <div className="w-full aspect-[9/16] max-h-[70vh] overflow-hidden bg-black">
          <video
            autoPlay
            loop
            muted
            playsInline
            poster={VIDEO_POSTER}
            className="w-full h-full object-cover"
            data-testid="demo-video"
          >
            <source src={DEMO_VIDEO} type="video/mp4" />
          </video>
          {/* Gradient overlay at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
        </div>
      </section>

      {/* Hero Text */}
      <section className="relative py-8 px-4 -mt-20 z-10">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Get Your Free Estimate
            </h1>
            <p className="text-zinc-400 mb-6">
              Dry Ice Blasting • No Water • No Chemicals • No Mess
            </p>
            
            {/* Quick Benefits */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { icon: Clock, text: 'Same Day Response' },
                { icon: Shield, text: 'Free On-Site Demo' },
                { icon: Droplets, text: 'Zero Water' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <item.icon className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <span className="text-xs text-zinc-400">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service Selection - Visual Grid */}
      <section className="px-4 pb-8">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl uppercase tracking-wide font-bold text-white mb-4 text-center" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            1. What Do You Need Cleaned?
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => setSelectedService(service.id)}
                className={`relative aspect-square overflow-hidden border-2 transition-all ${
                  selectedService === service.id
                    ? 'border-orange-500'
                    : 'border-zinc-800 hover:border-zinc-600'
                }`}
                data-testid={`service-select-${service.id}`}
              >
                <img 
                  src={service.img} 
                  alt={service.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <span className="text-white text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    {service.name}
                  </span>
                </div>
                {selectedService === service.id && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-black" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Upload */}
      <section className="px-4 pb-8">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl uppercase tracking-wide font-bold text-white mb-4 text-center" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            2. Upload Photos (Optional)
          </h2>
          <p className="text-zinc-500 text-sm text-center mb-4">
            Show us what needs cleaning for an accurate estimate
          </p>
          
          <div className="bg-zinc-950 border border-zinc-800 p-6">
            {/* Upload Button */}
            <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-zinc-700 hover:border-orange-500 cursor-pointer transition-colors">
              <Camera className="w-8 h-8 text-zinc-500 mb-2" />
              <span className="text-zinc-400 text-sm">Tap to upload photos</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                data-testid="photo-upload-input"
              />
            </label>
            
            {/* Preview Images */}
            {uploadedImages.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {uploadedImages.map((img, index) => (
                  <div key={index} className="relative aspect-square">
                    <img 
                      src={img.preview} 
                      alt={`Upload ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/80 rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="px-4 pb-8">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl uppercase tracking-wide font-bold text-white mb-4 text-center" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            3. Your Contact Info
          </h2>
          
          <form onSubmit={handleSubmit} className="bg-zinc-950 border border-zinc-800 p-6 space-y-4">
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your Name *"
                className="w-full bg-black border border-zinc-800 text-white p-4 focus:border-orange-500 focus:outline-none"
                data-testid="input-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="Phone *"
                className="w-full bg-black border border-zinc-800 text-white p-4 focus:border-orange-500 focus:outline-none"
                data-testid="input-phone"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Email *"
                className="w-full bg-black border border-zinc-800 text-white p-4 focus:border-orange-500 focus:outline-none"
                data-testid="input-email"
              />
            </div>
            
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Company Name (Optional)"
              className="w-full bg-black border border-zinc-800 text-white p-4 focus:border-orange-500 focus:outline-none"
              data-testid="input-company"
            />
            
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={3}
              placeholder="Describe what you need cleaned *"
              className="w-full bg-black border border-zinc-800 text-white p-4 focus:border-orange-500 focus:outline-none resize-none"
              data-testid="input-description"
            />
            
            {/* Preferred Contact Method */}
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                Preferred Contact Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                {['phone', 'email'].map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, preferred_contact: method }))}
                    className={`p-3 border text-sm uppercase tracking-wide transition-all ${
                      formData.preferred_contact === method
                        ? 'border-orange-500 bg-orange-500/10 text-orange-500'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    {method === 'phone' ? 'Call Me' : 'Email Me'}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Urgency */}
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                How Soon Do You Need This?
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'urgent', label: 'ASAP' },
                  { id: 'standard', label: 'This Week' },
                  { id: 'flexible', label: 'Flexible' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, urgency: option.id }))}
                    className={`p-3 border text-xs uppercase tracking-wide transition-all ${
                      formData.urgency === option.id
                        ? 'border-orange-500 bg-orange-500/10 text-orange-500'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            
            {submitError && (
              <div className="bg-red-500/10 border border-red-500/50 p-4 text-red-500 text-sm text-center">
                {submitError}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-orange-600 text-black py-4 text-xl uppercase tracking-widest font-bold hover:bg-orange-500 transition-colors disabled:opacity-50"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="submit-estimate-btn"
            >
              {isSubmitting ? 'Sending...' : 'Get My Free Estimate'}
            </button>
          </form>
        </div>
      </section>

      {/* Contact Options */}
      <section className="px-4 pb-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center text-zinc-500 text-sm mb-4">
            Or contact us directly
          </div>
          <div className="grid grid-cols-2 gap-4">
            <a 
              href="tel:8172109693" 
              className="flex items-center justify-center gap-3 bg-zinc-950 border border-zinc-800 p-4 hover:border-orange-500 transition-colors"
              data-testid="call-btn"
            >
              <Phone className="w-5 h-5 text-orange-500" />
              <span className="text-white font-mono">(817) 210-9693</span>
            </a>
            <a 
              href="mailto:operations@23rdindustries.com" 
              className="flex items-center justify-center gap-3 bg-zinc-950 border border-zinc-800 p-4 hover:border-orange-500 transition-colors"
              data-testid="email-btn"
            >
              <Mail className="w-5 h-5 text-orange-500" />
              <span className="text-white text-sm">Email Us</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <img src={LOGO_URL} alt="23rd Industries" className="h-8 mx-auto mb-4" />
          <p className="text-zinc-500 text-sm mb-2">
            23rd Environmental Units LLC
          </p>
          <p className="text-zinc-600 text-xs">
            Minority-Owned • Woman-Owned • Serving DFW Metroplex
          </p>
          <div className="flex items-center justify-center gap-2 mt-4 text-zinc-600 text-xs">
            <MapPin className="w-3 h-3" />
            <span>Haltom City, TX</span>
          </div>
        </div>
      </footer>
    </div>
  );
};
